import { doc, setDoc, getDoc, collection, getDocs, query, orderBy, limit, Timestamp, where } from "firebase/firestore";
import { db } from "../firebase";
import Workout from "./Workout";

class WorkoutSession {
  constructor(userId, workoutId, data) {
    this.userId = userId;
    this.workoutId = workoutId;
    this.performedAt = data.performedAt || Timestamp.now();
    this.date = data.date; // "YYYY-MM-DD"
    this.result = data.result;
    this.resultUnit = data.resultUnit;
    this.notes = data.notes || "";
    this.perceivedEffort = data.perceivedEffort || null; // 1-10 RPE
    this.createdAt = data.createdAt || Timestamp.now();
  }

  static getCollectionPath(userId) {
    return `workouts/${userId}/sessions`;
  }

  // Create a new session
  static async create(userId, workoutId, data) {
    const sessionId = `${workoutId}_${data.date}_${Date.now()}`;
    const sessionRef = doc(db, WorkoutSession.getCollectionPath(userId), sessionId);

    const sessionData = {
      workoutId,
      performedAt: Timestamp.now(),
      date: data.date,
      result: data.result,
      resultUnit: data.resultUnit,
      notes: data.notes || "",
      perceivedEffort: data.perceivedEffort || null,
      createdAt: Timestamp.now(),
    };

    await setDoc(sessionRef, sessionData);

    // Update workout's lastPerformedAt
    await Workout.updateLastPerformed(userId, workoutId);

    return sessionId;
  }

  // Get all sessions for a specific workout
  static async getByWorkoutId(userId, workoutId) {
    const sessionsRef = collection(db, WorkoutSession.getCollectionPath(userId));
    const q = query(
      sessionsRef,
      where("workoutId", "==", workoutId),
      orderBy("performedAt", "desc")
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }

  // Get the most recent session for a workout
  static async getMostRecent(userId, workoutId) {
    const sessionsRef = collection(db, WorkoutSession.getCollectionPath(userId));
    const q = query(
      sessionsRef,
      where("workoutId", "==", workoutId),
      orderBy("performedAt", "desc"),
      limit(1)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  }

  // Get stats for a workout
  static async getStats(userId, workoutId) {
    const sessions = await WorkoutSession.getByWorkoutId(userId, workoutId);

    if (sessions.length === 0) {
      return {
        totalSessions: 0,
        personalBest: null,
        personalBestDate: null,
        average: null,
        trend: null,
        firstResult: null,
        lastResult: null,
      };
    }

    const results = sessions.map(s => s.result);
    const personalBest = Math.max(...results);
    const personalBestSession = sessions.find(s => s.result === personalBest);
    const average = results.reduce((a, b) => a + b, 0) / results.length;

    // Calculate trend (compare first half to second half average)
    let trend = null;
    if (sessions.length >= 2) {
      const firstResult = sessions[sessions.length - 1].result; // oldest
      const lastResult = sessions[0].result; // newest
      if (firstResult > 0) {
        trend = ((lastResult - firstResult) / firstResult) * 100;
      }
    }

    return {
      totalSessions: sessions.length,
      personalBest,
      personalBestDate: personalBestSession?.date,
      average: Math.round(average * 10) / 10,
      trend: trend !== null ? Math.round(trend) : null,
      firstResult: sessions.length > 0 ? sessions[sessions.length - 1].result : null,
      lastResult: sessions.length > 0 ? sessions[0].result : null,
    };
  }

  // Get all sessions for dashboard (recent across all workouts)
  static async getRecentSessions(userId, limitCount = 10) {
    const sessionsRef = collection(db, WorkoutSession.getCollectionPath(userId));
    const q = query(
      sessionsRef,
      orderBy("performedAt", "desc"),
      limit(limitCount)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }
}

export default WorkoutSession;
